import logger, { LogTagKey } from "./logger";

// Contentful feature flag gating the v2 registration endpoints + Turnstile (SPEED-3483)
export const TURNSTILE_SIGNUP_FLAG = "turnstileSignup";

export const TURNSTILE_TOKEN_HEADER = "X-Turnstile-Token";

// Pages can place an element with this id (see TurnstileWidget) immediately after their
// continue button: an interactive challenge then replaces the button (which is hidden while
// the challenge is visible). Without an anchor, the widget floats bottom-right.
export const TURNSTILE_CONTAINER_ID = "turnstile-widget-container";

// Actions must match the backend's per-endpoint expectation (bits-user-management SPEED-3481 doc §2.3)
export type TurnstileAction = "signup-session-create" | "signup-session-update" | "signup-session-complete";

const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TOKEN_TIMEOUT_MS = 20000;
// Once Cloudflare shows an interactive challenge the user needs time to complete it
const INTERACTIVE_TOKEN_TIMEOUT_MS = 120000;
const SERVER_ERROR_RETRY_DELAYS_MS = [1000, 3000];
// Keep the floating fallback widget above any app overlay/modal
const MAX_Z_INDEX = "2147483647";

interface TurnstileRenderParams {
  sitekey: string;
  action: TurnstileAction;
  appearance: "always" | "execute" | "interaction-only";
  callback: (token: string) => void;
  "error-callback": () => boolean;
  "timeout-callback": () => void;
  "before-interactive-callback": () => void;
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, params: TurnstileRenderParams) => string | undefined;
      remove: (widgetId: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

// Test-only seam: CRA inlines process.env at build time, so tests cannot vary the
// site key any other way. Pass null to restore the env-provided key.
let siteKeyOverride: string | null = null;
export const __setTurnstileSiteKeyForTesting = (key: string | null): void => {
  siteKeyOverride = key;
};

const getSiteKey = (): string => siteKeyOverride ?? (process.env.REACT_APP_TURNSTILE_SITE_KEY || "");

const loadTurnstileScript = (): Promise<void> => {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        scriptPromise = null;
        script.remove();
        reject(new Error("Failed to load Turnstile script"));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

// Fetch the script ahead of the first protected call so submit isn't delayed by it
export const preloadTurnstile = (): void => {
  if (!getSiteKey()) return;
  loadTurnstileScript().catch(() => undefined);
};

const renderAndExecute = (siteKey: string, action: TurnstileAction): Promise<string | undefined> =>
  new Promise((resolve) => {
    // The widget stays invisible unless Cloudflare decides an interactive challenge is needed,
    // so it must live in a visible spot: the page's inline anchor when present,
    // otherwise a floating bottom-right fallback
    const container = document.createElement("div");
    const anchor = document.getElementById(TURNSTILE_CONTAINER_ID);
    // The anchor sits immediately after the form's continue button; if Cloudflare shows an
    // interactive challenge, the button is hidden so the widget takes its place
    const replaceTarget = anchor?.previousElementSibling as HTMLElement | null;
    let replacedDisplay: string | null = null;
    const hideReplaceTarget = () => {
      if (replaceTarget && replacedDisplay === null) {
        replacedDisplay = replaceTarget.style.display;
        replaceTarget.style.display = "none";
      }
    };
    const restoreReplaceTarget = () => {
      if (replaceTarget && replacedDisplay !== null) {
        replaceTarget.style.display = replacedDisplay;
        replacedDisplay = null;
      }
    };
    if (anchor) {
      anchor.appendChild(container);
    } else {
      container.style.position = "fixed";
      container.style.bottom = "16px";
      container.style.right = "16px";
      container.style.zIndex = MAX_Z_INDEX;
      document.body.appendChild(container);
    }

    let widgetId: string | undefined;
    let settled = false;
    const settle = (token?: string) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        if (widgetId !== undefined) window.turnstile?.remove(widgetId);
      } catch (e) {
        // widget already gone — nothing to clean up
      }
      container.remove();
      restoreReplaceTarget();
      resolve(token);
    };
    let timer = setTimeout(() => settle(undefined), TOKEN_TIMEOUT_MS);
    const onBeforeInteractive = () => {
      // A late callback racing settle() must not hide the button again — nothing would restore it
      if (settled) return;
      clearTimeout(timer);
      timer = setTimeout(() => settle(undefined), INTERACTIVE_TOKEN_TIMEOUT_MS);
      hideReplaceTarget();
    };

    try {
      // A fresh widget per call guarantees a single-use token bound to the right action
      widgetId = window.turnstile?.render(container, {
        sitekey: siteKey,
        action,
        appearance: "interaction-only",
        callback: (token: string) => settle(token),
        "error-callback": () => {
          settle(undefined);
          return true;
        },
        "timeout-callback": () => settle(undefined),
        "before-interactive-callback": onBeforeInteractive,
      });
      if (widgetId === undefined) settle(undefined);
    } catch (e) {
      settle(undefined);
    }
  });

/**
 * Returns a fresh single-use Turnstile token for the given action, or undefined when one
 * cannot be obtained (no site key configured, script blocked, challenge error/timeout).
 * Callers proceed without the header in that case — accepted while the backend
 * TURNSTILE_MODE is off/observe; the backend metrics surface tokenless web traffic.
 */
export const getTurnstileToken = async (action: TurnstileAction): Promise<string | undefined> => {
  const siteKey = getSiteKey();
  if (!siteKey) return undefined;
  try {
    await loadTurnstileScript();
    const token = await renderAndExecute(siteKey, action);
    if (!token) {
      logger.info(`Turnstile token unavailable for ${action}`, LogTagKey.TkSignup, "Turnstile token unavailable");
    }
    return token;
  } catch (e) {
    logger.info(`Turnstile script unavailable for ${action}`, LogTagKey.TkSignup, "Turnstile script unavailable");
    return undefined;
  }
};

const turnstileHeaders = (token?: string): Record<string, string> => (token ? { [TURNSTILE_TOKEN_HEADER]: token } : {});

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const errorCodeOf = (err: any): string | undefined => err?.error?.code;

/**
 * Runs a protected registration call with a fresh Turnstile token, applying the
 * SPEED-3481 §6 error contract:
 * - TURNSTILE_INVALID (401): re-execute the widget once and retry; rethrow if it repeats
 * - ATTESTATION_SERVER_ERROR (500): retry with short backoff
 * - ATTESTATION_BAD_REQUEST (400): client wiring bug — report to Sentry, rethrow
 * Tokens are single-use, so every attempt gets its own token.
 */
export const withTurnstileRetry = async <T>(
  action: TurnstileAction,
  call: (headers: Record<string, string>) => Promise<T>
): Promise<T> => {
  let turnstileRetried = false;
  let serverErrorRetries = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await call(turnstileHeaders(await getTurnstileToken(action)));
    } catch (err: any) {
      const code = errorCodeOf(err);
      if (code === "TURNSTILE_INVALID" && !turnstileRetried) {
        turnstileRetried = true;
        continue;
      }
      if (code === "ATTESTATION_SERVER_ERROR" && serverErrorRetries < SERVER_ERROR_RETRY_DELAYS_MS.length) {
        await delay(SERVER_ERROR_RETRY_DELAYS_MS[serverErrorRetries]);
        serverErrorRetries += 1;
        continue;
      }
      if (code === "ATTESTATION_BAD_REQUEST") {
        logger.error(err, LogTagKey.TkSignup, `Turnstile client wiring bug on ${action} (ATTESTATION_BAD_REQUEST)`);
      }
      throw err;
    }
  }
};
